import { type EvmItemType, EvmOrderType } from '@/service/EVM/types';
import { type Address, type Hex, hexToBigInt } from 'viem';
import type { EIP712SchemaType } from './types';
import { type EvmOrderParameters } from '@/service/api/types';

export type EvmOrderSignatureStructure = {
    offer: {
        itemType: EvmItemType;
        startAmount: bigint;
        identifierOrCriteria: bigint;
        endAmount: bigint;
        token: Address;
    }[];
    orderType: EvmOrderType;
    zoneHash: Hex;
    salt: bigint;
    zone: Address;
    offerer: Address;
    conduitKey: Address;
    consideration: {
        itemType: EvmItemType;
        startAmount: bigint;
        identifierOrCriteria: bigint;
        recipient: `0x${string}`;
        endAmount: bigint;
        token: `0x${string}`;
    }[];
    startTime: bigint;
    endTime: bigint;
    counter: bigint;
    totalOriginalConsiderationItems: bigint;
};

export const EvmOrderEIPSchema = {
    types: {
        OrderComponents: [
            { name: 'offerer', type: 'address' },
            { name: 'zone', type: 'address' },
            { name: 'offer', type: 'OfferItem[]' },
            { name: 'consideration', type: 'ConsiderationItem[]' },
            { name: 'orderType', type: 'uint8' },
            { name: 'startTime', type: 'uint256' },
            { name: 'endTime', type: 'uint256' },
            { name: 'zoneHash', type: 'bytes32' },
            { name: 'salt', type: 'uint256' },
            { name: 'conduitKey', type: 'bytes32' },
            { name: 'counter', type: 'uint256' }
        ],
        OfferItem: [
            { name: 'itemType', type: 'uint8' },
            { name: 'token', type: 'address' },
            { name: 'identifierOrCriteria', type: 'uint256' },
            { name: 'startAmount', type: 'uint256' },
            { name: 'endAmount', type: 'uint256' }
        ],
        ConsiderationItem: [
            { name: 'itemType', type: 'uint8' },
            { name: 'token', type: 'address' },
            { name: 'identifierOrCriteria', type: 'uint256' },
            { name: 'startAmount', type: 'uint256' },
            { name: 'endAmount', type: 'uint256' },
            { name: 'recipient', type: 'address' }
        ]
    } as const,
    processor: data => ({
        ...data,
        offer: data.offer.map(item => ({
            ...item,
            identifierOrCriteria: BigInt(item.identifierOrCriteria),
            startAmount: BigInt(item.startAmount),
            endAmount: BigInt(item.endAmount)
        })),
        consideration: data.consideration.map(item => ({
            ...item,
            identifierOrCriteria: BigInt(item.identifierOrCriteria),
            startAmount: BigInt(item.startAmount),
            endAmount: BigInt(item.endAmount)
        })),
        startTime: BigInt(data.startTime),
        endTime: BigInt(data.endTime),
        salt: hexToBigInt(data.salt),
        counter: BigInt(data.counter),
        totalOriginalConsiderationItems: BigInt(data.totalOriginalConsiderationItems)
    })
} as const satisfies EIP712SchemaType<EvmOrderParameters, EvmOrderSignatureStructure>;
